import { useTheme } from "styled-components";

import { buildPeopleMapLayer, getRefreshBucketStart } from "@lib/logic";
import { getWorkspaceOccupancyForBuildingAndFloorId, useAppSelector } from "@lib/store";
import { dateToLocalDateTimeString } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useRequiredContext } from "@lib/utils";
import { useUserProfilePics } from "@hooks";
import { toMapViewUser } from "@utils";

import { useFacilityMarkers } from "../../../organisms/hereAndNow/useFacilityMarkers";
import { HaNContext } from "../shell/HaNContext";
import { HaNMapStageContext, useHaNMapLayer } from "../shell/regions/HaNMapStageContext";

export const PeopleMap = () => {
  const { tab } = useRequiredContext(HaNContext);
  const { floorId } = useRequiredContext(HaNMapStageContext);

  if (tab !== "people" || !floorId) return null;

  return <PeopleMapLayer floorId={floorId} />;
};

type PeopleMapLayerProps = {
  floorId: string;
};

// Builds the tab's layer for the page's one map, which the map stage draws
const PeopleMapLayer = ({ floorId }: PeopleMapLayerProps) => {
  const theme = useTheme();
  const { building, bucket, people } = useRequiredContext(HaNContext);
  const buildingId = building.buildingId ?? "";

  const sensorData = useAppSelector(state => getWorkspaceOccupancyForBuildingAndFloorId(state, buildingId, floorId));
  const facilityMarkers = useFacilityMarkers(buildingId, floorId);

  // Only the floor's people, and only those with a picture
  const profilePictures = useUserProfilePics(
    people.reservations
      .filter(({ workspace }) => workspace.floorId === floorId)
      .map(({ owner }) => ({ userId: owner.id, imageHash: owner.imageHash ?? "" })),
  );

  const layer = buildPeopleMapLayer({
    floorId,
    reservations: people.reservations,
    sensorData,
    // The refresh bucket's start, so "now" moves along every 5 minutes while the page is open, with the data
    now: dateToLocalDateTimeString(new Date(getRefreshBucketStart(bucket))),
    toAvatar: owner => {
      return toMapViewUser(
        { email: owner.email, initials: owner.initials ?? "", imageUrl: profilePictures[owner.id] },
        theme,
      );
    },
    facilityMarkers,
  });

  useHaNMapLayer(layer);

  return null;
};
